import { useState, useEffect, useRef } from 'react';
import { RotateCcw, ChevronDown, Check, Minus, Plus } from 'lucide-react';
import { usePageTitle } from '../../../hooks/usePageTitle';
import DashboardLayout from '../../../layouts/DashboardLayout/DashboardLayout';
import Pagination from '../../../components/Pagination/Pagination';
import PageFooter from '../../../components/PageFooter/PageFooter';
import styles from './RiskCockpit.module.css';

const ACCOUNTS = ['All accounts', 'Fx Main Line', 'Fx Line One', 'Futures Main'];

const POSITIONS = [
  { id: 1, instrument: 'EURUSD', side: 'Long',  netVolume:  131.65,  notional: '$15,313,923', openPL:  904,    pctEquity: -0.02 },
  { id: 2, instrument: 'USDCAD', side: 'Short', netVolume: -161.87,  notional: '$16,187,000', openPL: -418724, pctEquity: +0.01 },
  { id: 3, instrument: 'EURUSD', side: 'Long',  netVolume:  131.65,  notional: '$15,313,923', openPL:  904,    pctEquity: -0.02 },
  { id: 4, instrument: 'USDCAD', side: 'Short', netVolume: -161.87,  notional: '$16,187,000', openPL: -418724, pctEquity: +0.01 },
  { id: 5, instrument: 'EURUSD', side: 'Long',  netVolume:  131.65,  notional: '$15,313,923', openPL:  904,    pctEquity: -0.02 },
  { id: 6, instrument: 'USDCAD', side: 'Short', netVolume: -161.87,  notional: '$16,187,000', openPL: -418724, pctEquity: +0.01 },
  { id: 7, instrument: 'EURUSD', side: 'Long',  netVolume:  131.65,  notional: '$15,313,923', openPL:  904,    pctEquity: -0.02 },
  { id: 8, instrument: 'USDCAD', side: 'Short', netVolume: -161.87,  notional: '$16,187,000', openPL: -418724, pctEquity: +0.01 },
];

const CURRENCIES = [
  { currency: 'USD', value: -559.1 },
  { currency: 'EUR', value: +283.7 },
  { currency: 'CAD', value: -559.1 },
  { currency: 'GBP', value: +283.7 },
  { currency: 'AUD', value: +283.7 },
  { currency: 'NZD', value: -559.1 },
];

const MAX_EXPOSURE = 559.1;
const POSITIONS_PAGE_SIZE = 8;

const HEATMAP = [
  { symbol: 'EURUSD', value: -618.7, level: 'lossHigh' },
  { symbol: 'GBPUSD', value: -618.7, level: 'lossHigh' },
  { symbol: 'USDCAD', value: -618.7, level: 'lossLow'  },
  { symbol: 'AUDUSD', value: -618.7, level: 'lossLow'  },
  { symbol: 'USDJPY', value: -618.7, level: 'lossLow'  },
  { symbol: 'NZDUSD', value: -618.7, level: 'lossLow'  },
  { symbol: 'EURGBP', value: -618.7, level: 'gainLow'  },
  { symbol: 'USDCHF', value: -618.7, level: 'lossLow'  },
  { symbol: 'EURJPY', value: -618.7, level: 'gainHigh' },
  { symbol: 'GBPJPY', value: -618.7, level: 'lossHigh' },
];

function fmt(v) {
  if (v >= 0) return `+${v.toLocaleString()}`;
  return v.toLocaleString();
}

function fmtPct(v) {
  const abs = Math.abs(v).toFixed(2);
  return v >= 0 ? `+${abs}%` : `-${abs}%`;
}

function CurrencyBar({ value }) {
  const pct = Math.abs(value) / MAX_EXPOSURE * 100;
  const isShort = value < 0;
  return (
    <div className={styles.barTrack}>
      <div className={styles.barLeft}>
        {isShort && (
          <div className={styles.barFillShort}>
            <span className={styles.barLabelShort}>{value}</span>
          </div>
        )}
      </div>
      <div className={`${styles.barDivider} ${isShort ? styles.barDividerShort : styles.barDividerLong}`} />
      <div className={styles.barRight}>
        {!isShort && (
          <div className={styles.barFillLong}>
            <span className={styles.barLabelLong}>+{value}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function RiskCockpit() {
  usePageTitle('Risk Cockpit');
  const [selectedAccount, setSelectedAccount] = useState('All accounts');
  const [accountOpen, setAccountOpen] = useState(false);
  const [time, setTime] = useState('');
  const [page, setPage] = useState(1);
  const dropdownRef = useRef(null);

  const positionsTotalPages = Math.max(1, Math.ceil(POSITIONS.length / POSITIONS_PAGE_SIZE));
  const positionsCurrentPage = Math.min(page, positionsTotalPages);
  const positionsRows = POSITIONS.slice((positionsCurrentPage - 1) * POSITIONS_PAGE_SIZE, positionsCurrentPage * POSITIONS_PAGE_SIZE);

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setTime(now.toTimeString().slice(0, 8));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setAccountOpen(false);
      }
    };
    if (accountOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [accountOpen]);

  return (
    <DashboardLayout>
      <div className={styles.page}>

        {/* Header */}
        <div className={styles.header}>
          <div>
            <h1 className={styles.pageTitle}>
              Risk cockpit
               <RotateCcw  size={20} className={styles.refreshIcon} />
            </h1>
            <p className={styles.subtitle}>
              <span className={styles.liveDot} />
              London · NY overlap · Time: {time}
            </p>
          </div>

          <div className={styles.accountWrap} ref={dropdownRef}>
            <button
              className={styles.accountBtn}
              onClick={() => setAccountOpen(o => !o)}
            >
              {selectedAccount.toUpperCase()}
              <ChevronDown size={13} className={accountOpen ? styles.chevronOpen : ''} />
            </button>
            {accountOpen && (
              <div className={styles.accountDropdown}>
                {ACCOUNTS.map(a => (
                  <button
                    key={a}
                    className={styles.accountOption}
                    onClick={() => { setSelectedAccount(a); setAccountOpen(false); }}
                  >
                    <span className={styles.checkSlot}>
                      {selectedAccount === a && <Check size={13} />}
                    </span>
                    {a}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Stat cards */}
        <div className={styles.statRow}>
          <div className={`${styles.statCard} ${styles.statCardLeft}`}>
            <p className={styles.statLabel}>NET EQUITY</p>
            <h2 className={styles.statValue}>$69,357,520</h2>
            <p className={`${styles.statSub} ${styles.negative}`}>-11,287 (0.02% today)</p>
          </div>
          <div className={`${styles.statCard} ${styles.statCardRight}`}>
            <p className={styles.statLabel}>OPEN P&L</p>
            <h2 className={`${styles.statValue} ${styles.negative}`}>-$1,091,233</h2>
            <p className={styles.statSub}>on 10,497 open positions</p>
          </div>
        </div>

        {/* Positions per instruments */}
        <div className={styles.positionsSection}>
        <p className={styles.sectionLabel}>POSITIONS PER INSTRUMENTS</p>
        <div className={`${styles.card} ${styles.positionsCard} ${positionsTotalPages > 1 ? styles.paginated : ''}`}>
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Instrument</th>
                  <th className={styles.th}>Side</th>
                  <th className={`${styles.th} ${styles.right}`}>Net volume</th>
                  <th className={`${styles.th} ${styles.right}`}>Notional (USD)</th>
                  <th className={`${styles.th} ${styles.right}`}>Open P&L</th>
                  <th className={`${styles.th} ${styles.right}`}>% of equity</th>
                </tr>
              </thead>
              <tbody>
                {positionsRows.map(row => (
                  <tr key={row.id} className={styles.tr}>
                    <td className={`${styles.td} ${styles.bold}`}>{row.instrument}</td>
                    <td className={styles.td}>
                      <span className={`${styles.pill} ${row.side === 'Long' ? styles.pillLong : styles.pillShort}`}>
                        {row.side}
                      </span>
                    </td>
                    <td className={`${styles.td} ${styles.right}`}>{row.netVolume}</td>
                    <td className={`${styles.td} ${styles.right}`}>{row.notional}</td>
                    <td className={`${styles.td} ${styles.right} ${row.openPL >= 0 ? styles.positive : styles.negative}`}>
                      {fmt(row.openPL)}
                    </td>
                    <td className={`${styles.td} ${styles.right} ${row.pctEquity >= 0 ? styles.positive : styles.negative}`}>
                      {fmtPct(row.pctEquity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={positionsCurrentPage} totalPages={positionsTotalPages} onPageChange={setPage} variant="outline" />
        </div>
        </div>

        {/* Bottom two-column section */}
        <div className={styles.bottomGrid}>

          {/* Basket Currency Exposure */}
          <div className={`${styles.currencySection} d-none d-md-block`}>
            <p className={styles.sectionLabel}>BASKET CURRENCY EXPOSURE</p>
            <div className={styles.card}>
              <div className={styles.tableScroll}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Currency</th>
                      <th className={`${styles.th} ${styles.thCenter}`}>← Short · Net · Long →</th>
                      <th className={`${styles.th} ${styles.right}`}>Exposure</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CURRENCIES.map(({ currency, value }) => (
                      <tr key={currency} className={styles.tr}>
                        <td className={`${styles.td} ${styles.bold}`}>{currency}</td>
                        <td className={styles.td}>
                          <CurrencyBar value={value} />
                        </td>
                        <td className={`${styles.td} ${styles.right}`}>
                          <span className={styles.exposureLots}>
                            {value > 0 ? `+${value}` : value} lots
                          </span>
                          <span className={styles.exposureNotional}>$55.54M notional</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* P&L Heatmap */}
          <div>
            <p className={styles.sectionLabel}>P&L HEATMAP · INSTRUMENTS</p>
            <div className={`${styles.card} ${styles.heatmapCard}`}>
              <div className={styles.heatmapGrid}>
                {HEATMAP.map((tile, i) => (
                  <div key={i} className={`${styles.tile} ${styles[tile.level]}`}>
                    <span className={styles.tileSymbol}>{tile.symbol}</span>
                    <span className={styles.tileValue}>{tile.value}k</span>
                  </div>
                ))}
              </div>
              <div className={styles.heatmapLegend}>
                <span className={styles.legendText}>
                  <Minus size={16} strokeWidth={2.5} /> Loss
                </span>
                <span className={styles.legendSwatches}>
                  <span className={`${styles.swatch} ${styles.swatchLoss3}`} />
                  <span className={`${styles.swatch} ${styles.swatchLoss2}`} />
                  <span className={`${styles.swatch} ${styles.swatchLoss1}`} />
                  <span className={`${styles.swatch} ${styles.swatchGain2}`} />
                  <span className={`${styles.swatch} ${styles.swatchGain3}`} />
                </span>
                <span className={styles.legendText}>
                  Gain <Plus size={16} strokeWidth={2.5} />
                </span>
              </div>
            </div>
          </div>

        </div>

        <PageFooter />
      </div>
    </DashboardLayout>
  );
}

export default RiskCockpit;
